import { MODAL_DEFAULTS } from "../constants";

const DEFAULT_ANIMAL_EMOJIS = [
  "🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯", "🦁", "🐮",
  "🐷", "🐸", "🐵", "🐔", "🐧", "🐦", "🦉", "🦄", "🐝", "🦋", "🐢", "🐙",
];

export const ANIMAL_EMOJIS = DEFAULT_ANIMAL_EMOJIS;

function hashStringToUint32(str) {
  // FNV-1a 32-bit
  let hash = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export const getStableAnimalEmoji = (seed, emojis = DEFAULT_ANIMAL_EMOJIS) => {
  const safeSeed = String(seed ?? "");
  const list = Array.isArray(emojis) && emojis.length > 0 ? emojis : DEFAULT_ANIMAL_EMOJIS;
  const idx = hashStringToUint32(safeSeed) % list.length;
  return list[idx];
};

export const getRandomAnimalEmoji = (excludeEmoji, emojis = DEFAULT_ANIMAL_EMOJIS) => {
  const list = Array.isArray(emojis) && emojis.length > 0 ? emojis : DEFAULT_ANIMAL_EMOJIS;
  if (list.length === 1) return list[0];
  const filtered = excludeEmoji ? list.filter((e) => e !== excludeEmoji) : list;
  return filtered[Math.floor(Math.random() * filtered.length)];
};

export const amountDataToDisplay = (data) => {
  return `${(data / 100).toFixed(2)}`;
};

export const amountInputToData = (input) => {
  return Math.round(parseFloat(input) * 100);
};

export const toggleModal = (modalId, options = {}) => {
  const modalEl = document.getElementById(modalId);
  if (!modalEl) return;

  // Compute content: caller options override defaults
  const defaults = MODAL_DEFAULTS[modalId];

  // Only override modal DOM when caller provided options or defaults exist
  const shouldInject =
    (options && Object.keys(options).length > 0) || (defaults && Object.keys(defaults).length > 0);

  if (shouldInject) {
    const title = options.title || (defaults && defaults.title) || "Error";
    const message = options.message || (defaults && defaults.message) || "";
    const btnTitle = options.btnTitle || (defaults && defaults.btnTitle) || "Close";

    try {
      const titleEl = modalEl.querySelector(".modal-title");
      if (titleEl) titleEl.textContent = title;

      const bodyEl = modalEl.querySelector(".modal-body");
      if (bodyEl) bodyEl.innerHTML = message ? `<p>${message}</p>` : "";

      const btnEl = modalEl.querySelector(".modal-footer .btn-secondary");
      if (btnEl) btnEl.textContent = btnTitle;
    } catch (e) {
      console.error(e);
    }
  }

  const modal = new window.bootstrap.Modal(modalEl);
  modal.toggle();
};

/* ------------------------------------------------------------------ */
/* Image helpers (client-side optimization)                             */
/* ------------------------------------------------------------------ */

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

const drawDownscaledCanvas = (img, { maxDimension }) => {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const maxSide = Math.max(w, h);
  const scale = maxSide > maxDimension ? maxDimension / maxSide : 1;

  const targetW = Math.max(1, Math.round(w * scale));
  const targetH = Math.max(1, Math.round(h * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.drawImage(img, 0, 0, targetW, targetH);

  return { canvas, didDownscale: scale < 1 };
};

const canvasToBlob = (canvas, type) =>
  new Promise((resolve, reject) => {
    if (!canvas.toBlob) return reject(new Error("canvas.toBlob not supported"));
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error("Failed to encode image"));
      resolve(blob);
    }, type);
  });

const replaceExt = (filename, extNoDot) => {
  const base = String(filename || "image").replace(/\.[^/.]+$/, "");
  return `${base}.${extNoDot}`;
};

/**
 * Downscale an image file and encode as PNG if it exceeds the given byte threshold.
 * - Non-image files (e.g. PDFs) are returned unchanged.
 * - Size reduction comes from downscaling (PNG "quality" isn't configurable in browsers).
 */
export const optimizeImageToPngIfOverBytes = async (
  file,
  { thresholdBytes = 1 * 1024 * 1024, maxDimension = 1600 } = {}
) => {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  if (file.size <= thresholdBytes) return file;

  const img = await loadImageFromFile(file);
  const { canvas, didDownscale } = drawDownscaledCanvas(img, { maxDimension });
  const blob = await canvasToBlob(canvas, "image/png");

  const out = new File([blob], replaceExt(file.name, "png"), {
    type: "image/png",
    lastModified: Date.now(),
  });

  // If we didn't downscale and png got bigger, just keep original.
  if (!didDownscale && out.size >= file.size) return file;
  return out;
};
